import { useState } from 'react'
import type { Medication, StockAdjustment } from '../../store/medicationsSlice'
import { AdjustStockModal } from './AdjustStockModal'

interface StockManagementProps {
  medication: Medication
  adjustments: StockAdjustment[]
  onAdjustStock: (data: { medication_id: string; adjustment: number; new_quantity: number; reason: string; adjusted_by: string }) => Promise<void>
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export function StockManagement({ medication, adjustments, onAdjustStock }: StockManagementProps) {
  const [modalOpen, setModalOpen] = useState(false)

  const handleSave = async (data: { adjustment: number; new_quantity: number; reason: string; adjusted_by: string }) => {
    await onAdjustStock({
      medication_id: medication.id,
      ...data,
    })
    setModalOpen(false)
  }

  return (
    <div data-testid="stock-management-section" className="bg-surface border border-border rounded-[6px]">
      <div className="flex items-center justify-between border-b border-border-light px-4 py-2.5">
        <h2 className="text-[14px] font-medium text-text-primary">Stock Management</h2>
        <button
          data-testid="adjust-stock-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center h-7 px-3 rounded-[4px] border border-border text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          Adjust Stock
        </button>
      </div>

      <div className="px-4 py-3 max-sm:px-3">
        <div className="flex gap-8 mb-4">
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Current Quantity</span>
            <span className="text-[16px] font-semibold text-text-primary" data-testid="stock-current-quantity">
              {medication.stock_quantity}
            </span>
          </div>
          <div>
            <span className="text-[11px] text-text-muted block mb-0.5">Reorder Threshold</span>
            <span className="text-[16px] font-semibold text-text-primary" data-testid="stock-reorder-threshold">
              {medication.reorder_threshold}
            </span>
          </div>
        </div>

        <div>
          <h3 className="text-[13px] font-medium text-text-primary mb-2">Adjustment History</h3>
          {adjustments.length === 0 ? (
            <p className="text-[13px] text-text-disabled italic" data-testid="stock-history-empty">
              No adjustments recorded
            </p>
          ) : (
            <div data-testid="stock-adjustment-history" className="border border-border rounded-[4px]">
              <div className="sa-table-header grid grid-cols-[1fr_80px_100px_1fr_120px] gap-2 px-3 py-2 border-b border-border-light text-[11px] text-text-muted font-medium">
                <span>Date</span>
                <span>Adjustment</span>
                <span>New Qty</span>
                <span>Reason</span>
                <span>Adjusted By</span>
              </div>
              {adjustments.map((adj) => (
                <div
                  key={adj.id}
                  data-testid="stock-adjustment-row"
                  className="sa-table-row grid grid-cols-[1fr_80px_100px_1fr_120px] gap-2 px-3 py-2 border-b border-border-light last:border-b-0 text-[13px] text-text-primary hover:bg-hover transition-colors duration-100"
                >
                  <span>{formatDate(adj.created_at)}</span>
                  <span className={adj.adjustment > 0 ? 'text-status-success' : 'text-status-error'}>
                    {adj.adjustment > 0 ? `+${adj.adjustment}` : adj.adjustment}
                  </span>
                  <span>{adj.new_quantity}</span>
                  <span className="text-text-muted">{adj.reason}</span>
                  <span className="text-text-muted">{adj.adjusted_by}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <AdjustStockModal
          currentQuantity={medication.stock_quantity}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}
